import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { StatCard } from '../components/ui/StatCard';
import { api } from '../services/api';

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DashboardPage() {
  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.getDashboard(),
    refetchInterval: 30000,
  });

  const { data: callQueue } = useQuery({
    queryKey: ['callQueue'],
    queryFn: () => api.getCallQueueStats(),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Ecosistema Financiero IA" />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard
          icon="💸"
          value={formatMoney(dashboard?.expense_summary?.month_total || 0)}
          label="Gastos este mes"
        />
        <StatCard
          icon="👥"
          value={dashboard?.tandas_summary?.active || 0}
          label="Tandas activas"
        />
        <StatCard
          icon="💡"
          value={dashboard?.services_summary?.due_soon || 0}
          label="Servicios por vencer"
        />
        <StatCard
          icon="💵"
          value={formatMoney(dashboard?.loans_summary?.total_outstanding || 0)}
          label="Por cobrar"
        />
        <StatCard
          icon="⚠️"
          value={dashboard?.loans_summary?.overdue_count || 0}
          label="Vencidos"
          variant="warning"
        />
        <StatCard
          icon="📞"
          value={callQueue?.pending || 0}
          label="En cola"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Categories */}
        <div className="card">
          <h2 className="text-lg font-semibold text-primary-500 mb-4 flex items-center gap-2">
            📊 Gastos por Categoría
          </h2>
          <div className="space-y-3">
            {dashboard?.expense_summary?.by_category?.length ? (
              dashboard.expense_summary.by_category.map((cat) => {
                const percentage = cat.budget > 0
                  ? Math.min(100, (cat.total / cat.budget) * 100)
                  : 0;
                const colorClass = percentage > 90
                  ? 'bg-red-500'
                  : percentage > 70
                  ? 'bg-yellow-500'
                  : 'bg-primary-500';

                return (
                  <div
                    key={cat.name}
                    className="flex items-center gap-3 p-3 bg-dark-bg rounded-lg"
                  >
                    <span className="text-xl">{cat.icon || '📁'}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm">
                        <span>{cat.name}</span>
                        <span className="text-primary-500 font-medium">
                          {formatMoney(cat.total)}
                        </span>
                      </div>
                      {cat.budget > 0 && (
                        <div className="h-1.5 bg-dark-border rounded-full mt-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${colorClass}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">Sin gastos registrados</p>
            )}
          </div>
        </div>

        {/* Upcoming Dues */}
        <div className="card">
          <h2 className="text-lg font-semibold text-primary-500 mb-4 flex items-center gap-2">
            📅 Próximos Vencimientos
          </h2>
          <div className="space-y-3">
            {dashboard?.services_summary?.due_soon ? (
              <p className="text-gray-400">
                {dashboard.services_summary.due_soon} servicios por vencer esta semana
              </p>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No hay vencimientos próximos
              </p>
            )}

            {dashboard?.loans_summary?.overdue_count ? (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400">
                  ⚠️ {dashboard.loans_summary.overdue_count} préstamos vencidos
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
